import React, { useState, useEffect } from 'react';
import CameraCapture from './CameraCapture';
import PhotoViewer from './PhotoViewer';
import './EquipmentForm.css';

const equipmentFields = {
  'existing-pole': [
    { name: 'height', label: 'Pole Height (ft)', type: 'number' },
    { name: 'class', label: 'Pole Class', type: 'text' },
    { name: 'material', label: 'Material', type: 'select', options: ['Wood', 'Steel', 'Concrete'] }
  ],
  'pole': [
    { name: 'height', label: 'Pole Height (ft)', type: 'number' },
    { name: 'class', label: 'Pole Class', type: 'text' },
    { name: 'material', label: 'Material', type: 'select', options: ['Wood', 'Steel', 'Concrete'] }
  ],
  'transformer': [
    { name: 'kva', label: 'KVA Rating', type: 'number' },
    { name: 'phase', label: 'Phase', type: 'select', options: ['Single', 'Three'] },
    { name: 'mounting', label: 'Mounting Type', type: 'select', options: ['Pole', 'Pad', 'Platform'] }
  ],
  'anchor': [
    { name: 'size', label: 'Size', type: 'text' },
    { name: 'lead', label: 'Lead (ft)', type: 'number' }
  ],
  'switch': [
    { name: 'size', label: 'Size (amps)', type: 'number' }
  ],
  'terminating-cabinet': [
    { name: 'phase', label: 'Phase', type: 'select', options: ['Single', 'Three'] }
  ],
  'secondary-pedestal': [],
  'meter': []
};

const MAX_PHOTOS = 3; // Limit photos per equipment item to prevent Firestore size issues

const EquipmentForm = ({ type, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({});
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);

  // Cleanup effect to ensure camera is closed when form is unmounted
  useEffect(() => {
    return () => {
      // Force close camera when form is unmounted
      setShowCamera(false);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ensure camera is closed before submitting
    setShowCamera(false);
    onSubmit({ ...formData, notes, photos });
  };

  const handleClose = () => {
    // Ensure camera is closed when form is closed
    setShowCamera(false);
    onClose();
  };

  const handlePhotoCapture = (photoData) => {
    if (photos.length >= MAX_PHOTOS) {
      alert(`Maximum ${MAX_PHOTOS} photos allowed per equipment item.`);
      return;
    }
    setPhotos([...photos, photoData]);
  };

  const handlePhotoDelete = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
  };

  const fields = equipmentFields[type] || [];

  return (
    <>
      <div className="equipment-form-container">
        <div className="equipment-form-content">
          <div className="equipment-form-header">
            <h2>{type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Details</h2>
            <button className="close-button" onClick={handleClose}>&times;</button>
          </div>
          <form onSubmit={handleSubmit}>
            {fields.map((field) => (
              <div key={field.name} className="form-field">
                <label htmlFor={field.name}>{field.label}</label>
                {field.type === 'select' ? (
                  <select
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    required
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    required
                  />
                )}
              </div>
            ))}
            <div className="form-field">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes here..."
                rows={4}
              />
            </div>
            
            {/* Photo Section */}
            <div className="form-field">
              <label>Photos ({photos.length}/{MAX_PHOTOS})</label>
              <div className="photo-section">
                {photos.length < MAX_PHOTOS && (
                  <button 
                    type="button" 
                    className="add-photo-button"
                    onClick={() => setShowCamera(true)}
                  >
                    Add Photo
                  </button>
                )}
                {photos.length > 0 && (
                  <button 
                    type="button" 
                    className="view-photos-button"
                    onClick={() => setShowPhotoViewer(true)}
                  >
                    View Photos ({photos.length})
                  </button>
                )}
                {photos.length >= MAX_PHOTOS && (
                  <div className="photo-limit-message">
                    Maximum {MAX_PHOTOS} photos reached
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={handleClose}>Cancel</button>
              <button type="submit" className="submit-button">Add Equipment</button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Camera Modal */}
      {showCamera && (
        <CameraCapture
          onCapture={handlePhotoCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
      
      {/* Photo Viewer Modal */}
      {showPhotoViewer && (
        <PhotoViewer
          photos={photos}
          onClose={() => setShowPhotoViewer(false)}
          onDelete={handlePhotoDelete}
        />
      )}
    </>
  );
};

export default EquipmentForm; 